/**
 * Main App Component
 * Sets up routing and context providers
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClassesPage } from './pages/ClassesPage';
import { CreateClassPage } from './pages/CreateClassPage';
import { EditClassPage } from './pages/EditClassPage';
import { ClassDetailPage } from './pages/ClassDetailPage';
import { StudentsPage } from './pages/StudentsPage';
import { CreateStudentPage } from './pages/CreateStudentPage';
import { EditStudentPage } from './pages/EditStudentPage';
import { StudentDetailPage } from './pages/StudentDetailPage';
import { WordListsPage } from './pages/WordListsPage';
import { CreateWordListPage } from './pages/CreateWordListPage';
import { EditWordListPage } from './pages/EditWordListPage';
import { WordListDetailPage } from './pages/WordListDetailPage';
import { TeachersPage } from './pages/TeachersPage';
import { CreateTeacherPage } from './pages/CreateTeacherPage';
import { TeacherDetailPage } from './pages/TeacherDetailPage';
import { ManageParentAccountsPage } from './pages/ManageParentAccountsPage';
import { CreateParentAccountPage } from './pages/CreateParentAccountPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter
        basename="/admin"
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ThemeProvider>
          <NotificationProvider>
            <AuthProvider>
              <ViewModeProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />
                
                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DashboardPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/classes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ClassesPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/classes/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateClassPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/classes/:classId/edit"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditClassPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/classes/:classId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ClassDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <StudentsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateStudentPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId/edit"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditStudentPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <StudentDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId/parents"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ManageParentAccountsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId/parents/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateParentAccountPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/wordlists"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <WordListsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/wordlists/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateWordListPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/wordlists/:wordListId/edit"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditWordListPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/wordlists/:wordListId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <WordListDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/teachers"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <TeachersPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/teachers/new"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <CreateTeacherPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/teachers/:teacherId"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <TeacherDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ProfilePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <SettingsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                {/* Redirect root to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                
                {/* 404 - Redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </ViewModeProvider>
          </AuthProvider>
        </NotificationProvider>
      </ThemeProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
